// @mui
import { SxProps, Theme } from '@mui/material';
import 'simplebar-react/dist/simplebar.min.css';
import Box from '@mui/material/Box';
import { alpha, styled } from '@mui/material/styles';
import { PropsWithChildren, memo } from 'react';
import SimpleBarReact, { Props as ScrollbarProps } from 'simplebar-react';

const RootStyle = styled('div', { shouldForwardProp: (prop) => prop !== 'fillContainer' })<{ fillContainer: boolean }>(
  ({ fillContainer }) => ({
    flexGrow: fillContainer ? 1 : 0,
    height: fillContainer ? '100%' : 'auto',
    overflow: 'hidden',
  }),
);

const SimpleBarStyle = styled(SimpleBarReact)(({ theme }) => ({
  maxHeight: '100%',
  '& .simplebar-scrollbar': {
    '&:before': {
      backgroundColor: alpha(theme.palette.grey[600], 0.48),
    },
    '&.simplebar-visible:before': {
      opacity: 1,
    },
  },
  '& .simplebar-track.simplebar-vertical': {
    width: 10,
  },
  '& .simplebar-track.simplebar-horizontal .simplebar-scrollbar': {
    height: 6,
  },
  '& .simplebar-mask': {
    zIndex: 'inherit',
  },
}));

interface Props extends ScrollbarProps {
  sx?: SxProps<Theme>;
  /**
   * Whether the scroll area should stretch to fill its parent's height (the
   * default, e.g. a drawer/sidebar list). `false` keeps it sized to its own
   * content instead — needed wherever the parent's height isn't meant to
   * bound the scroll area, since a parent that ends up taller than the
   * content (e.g. via a flex sibling) would otherwise leave the horizontal
   * scrollbar sitting below the content in that leftover space.
   */
  fillContainer?: boolean;
}

/**
 * Minimal@4 component.
 */
function Scrollbar({ children, sx, fillContainer = true, ...other }: PropsWithChildren<Props>) {
  const userAgent = typeof navigator === 'undefined' ? 'SSR' : navigator.userAgent;

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);

  if (isMobile) {
    return (
      <Box sx={{ overflowX: 'auto', ...sx }} {...other}>
        {children}
      </Box>
    );
  }

  return (
    <RootStyle fillContainer={fillContainer}>
      <SimpleBarStyle timeout={500} clickOnTrack={false} sx={sx} {...other}>
        {children}
      </SimpleBarStyle>
    </RootStyle>
  );
}

export default memo(Scrollbar);
